import { CartAddEvent, ThemeEvents } from '@theme/events';

/*
 * eac-perso.js — Personalizacion con sobreprecio real via productos-fee ocultos.
 *
 * Dos responsabilidades:
 *  1) PDP: recalcula el precio en vivo y, al anadir al carrito, mete el producto
 *     principal + los fees elegidos (Trail Eco / Excellence / Texto) en una sola
 *     llamada, vinculados con un token `_perso_id`.
 *  2) Carrito (cualquier pagina/drawer): oculta las lineas-fee, las sincroniza
 *     con la cantidad del producto padre y las elimina si se quita el padre.
 *
 * El dinero se cobra de verdad porque cada fee es una linea real; el cliente no
 * puede tocarla porque la ocultamos y la reconciliamos en cada cambio del carrito.
 *
 * Y en TODAS las fichas (con o sin personalizacion): la talla marcada es la que
 * viaja al carrito, tambien desde el pack "comprados juntos" y el upsell.
 * De entrada no va ninguna marcada (oct-2026): si falta elegir, se avisa en el
 * propio grupo (Talla, Color...) y el form no sale con una talla que nadie eligio.
 *
 * Lo que se anade desde "Completa tu carrito" queda a la vista en el cajon (26/10).
 */

const FEE_PARENT_PROP = '_fee_parent';
const PERSO_ID_PROP = '_perso_id';
const FEE_LABEL_PROP = '_fee_label';

/* ---------- utilidades ---------- */

function formatMoney(cents) {
  const n = (Number(cents) / 100).toFixed(2).replace('.', ',');
  return n + ' €';
}

function genPersoId() {
  return 'p' + Date.now().toString(36) + Math.floor(Math.random() * 1e6).toString(36);
}

function cartItemsSectionIds() {
  const ids = [];
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
    const id = el.getAttribute('data-section-id');
    if (id && ids.indexOf(id) === -1) ids.push(id);
  });
  return ids;
}

async function getCart() {
  const r = await fetch(`${Theme.routes.cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } });
  return r.json();
}

/* ---------- Ficha: la talla elegida es la que va al carrito ---------- */

// El form de la ficha manda UNA variante (input name="id") y hay que mantenerla
// al dia con las opciones marcadas. Sin esto iba la primera talla disponible
// aunque el cliente eligiera otra (oct-2026: pedian una S y llegaba una XS).
// Devuelve null si falta alguna opcion o no hay coincidencia exacta: mejor avisar
// que meter en el carrito una talla que nadie ha elegido.
function findVariant(form, variants, optionNames) {
  if (!variants || !variants.length) return null;
  if (form.querySelector('input[name^="options["]') == null) return variants[0];
  const chosen = {};
  form.querySelectorAll('input[name^="options["]:checked').forEach((inp) => {
    const m = inp.name.match(/^options\[(.+)\]$/);
    if (m) chosen[m[1]] = inp.value;
  });
  return variants.find((v) => optionNames.every((name, i) => chosen[name] === v.options[i])) || null;
}

function formatPrice(cents) {
  return (Number(cents) / 100).toFixed(2).replace('.', ',') + '€';
}

// Refleja la variante en todo lo que depende de ella: el id que viaja en el form,
// el precio, el boton (agotada = no se puede anadir), la URL y el pack "comprados
// juntos", que manda su propio data-vid (ownIds = ids de las variantes de la ficha).
// Sin variante (falta elegir o esa combinacion no existe) el id no viaja, el precio
// vuelve al de la ficha ("Desde ...") y el boton dice por que no se puede anadir.
// data = los datos que pinta la seccion (precios con la moneda del mercado y los
// textos "Agotado" / "No disponible" del tema); sin ellos, los de siempre.
function applyVariant(form, variant, ownIds, updateUrl, data) {
  const d = data || {};
  const labels = d.labels || {};
  const idInput = form.querySelector('input[name="id"]');
  const scope = form.closest('.shopify-section') || document;
  const priceEl = scope.querySelector('.pdp-price');
  const btn = form.querySelector('.pdp-atc');
  let label = null;
  if (btn) {
    if (btn.dataset.eacLabel == null && !btn.disabled) btn.dataset.eacLabel = btn.textContent.trim();
    if (btn.dataset.eacLabel != null) label = btn.dataset.eacLabel;
  }

  if (!variant) {
    if (idInput) {
      idInput.value = '';
      idInput.disabled = true;
    }
    if (priceEl && d.priceNone) priceEl.textContent = d.priceNone;
    if (btn && label != null) {
      if (missingGroup(form)) {
        // falta elegir: el boton sigue activo y, al pulsarlo, se avisa en el grupo
        const any = !d.variants || d.variants.some((v) => v.available);
        btn.disabled = !any;
        btn.textContent = any ? label : labels.soldOut || 'Agotado';
      } else {
        btn.disabled = true;
        btn.textContent = labels.unavailable || 'No disponible';
      }
    }
    if (updateUrl) {
      const u = new URL(window.location.href);
      if (u.searchParams.has('variant')) {
        u.searchParams.delete('variant');
        history.replaceState(history.state, '', u.toString());
      }
    }
    return;
  }

  if (idInput) {
    idInput.value = variant.id;
    idInput.disabled = false;
  }
  if (priceEl) priceEl.textContent = variant.priceFormatted || formatPrice(variant.price);
  if (btn && label != null) {
    btn.disabled = !variant.available;
    btn.textContent = variant.available ? label : labels.soldOut || 'Agotado';
  }

  if (ownIds) {
    document.querySelectorAll('.eac-bundle__item[data-vid]').forEach((el) => {
      if (ownIds.has(Number(el.dataset.vid))) el.dataset.vid = String(variant.id);
    });
  }

  if (updateUrl) {
    const u = new URL(window.location.href);
    if (u.searchParams.get('variant') !== String(variant.id)) {
      u.searchParams.set('variant', variant.id);
      history.replaceState(history.state, '', u.toString());
    }
  }
}

function showFormError(form, msg) {
  const box = form.querySelector('#EacAtcError');
  if (!box) return;
  box.setAttribute('role', 'alert');
  box.textContent = msg;
  box.hidden = false;
}

function hideFormError(form) {
  const box = form.querySelector('#EacAtcError');
  if (box) box.hidden = true;
}

// Desplazamiento suave, salvo para quien pide menos movimiento.
function scrollBehavior() {
  return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
}

// Solo se mueve la pagina si el elemento no se ve entero (sin saltos innecesarios).
function ensureVisible(el) {
  if (!el) return;
  const r = el.getBoundingClientRect();
  if (r.top < 80 || r.bottom > window.innerHeight - 16) el.scrollIntoView({ behavior: scrollBehavior(), block: 'center' });
}

// Grupos de opciones de la ficha (Talla, Color...) y el primero sin elegir.
function optionGroups(form) {
  return Array.from(form.querySelectorAll('.pdp-opt')).filter((g) => g.querySelector('input[name^="options["]'));
}

function missingGroup(form) {
  return optionGroups(form).find((g) => !g.querySelector('input[name^="options["]:checked')) || null;
}

// Como se nombra cada opcion en el aviso (clave sin tildes ni mayusculas).
const CHOICE_NAMES = {
  talla: 'tu talla',
  color: 'el color',
  idioma: 'el idioma',
  genero: 'el género',
  modelo: 'el modelo',
  diseno: 'el diseño',
};

function choiceMessage(group) {
  const label = group.querySelector('.pdp-opt__label span');
  const shown = String((label && label.textContent) || group.dataset.name || '')
    .trim()
    .replace(/\s*:+$/, '');
  const key = shown
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  const known = CHOICE_NAMES[key] || (key.indexOf('talla') === 0 ? CHOICE_NAMES.talla : null);
  if (known) return 'Elige ' + known + ' para continuar.';
  return 'Elige una opción de «' + shown + '» para continuar.';
}

function clearMissing(group) {
  if (!group) return;
  group.classList.remove('is-missing');
  const msg = group.querySelector('.pdp-opt__msg');
  if (msg) {
    msg.hidden = true;
    msg.textContent = ''; // describe al grupo (aria-describedby): que no se quede el aviso viejo
  }
}

// Falta elegir: el aviso va en su grupo, que se resalta y se trae a la vista, y el
// foco pasa a su primer valor (con el teclado se elige con las flechas).
function promptChoice(form, group) {
  hideFormError(form);
  optionGroups(form).forEach((g) => {
    if (g !== group) clearMissing(g);
  });
  group.classList.add('is-missing');
  let msg = group.querySelector('.pdp-opt__msg');
  if (!msg) {
    msg = document.createElement('p');
    msg.className = 'pdp-opt__msg';
    msg.setAttribute('role', 'alert');
    group.appendChild(msg);
  }
  msg.textContent = choiceMessage(group);
  msg.hidden = false;
  ensureVisible(group);
  const first = group.querySelector('input[name^="options["]');
  if (first) first.focus({ preventScroll: true });
}

// El aviso general (agotada, no disponible...) va junto al boton: que se vea.
function revealFormError(form) {
  ensureVisible(form.querySelector('#EacAtcError'));
}

// Sin variante: avisa de lo que falta (o de que esa combinacion no existe).
function promptMissingOrUnavailable(form) {
  const g = missingGroup(form);
  if (g) {
    promptChoice(form, g);
    return;
  }
  showFormError(form, 'Esa combinación no está disponible. Elige otra.');
  revealFormError(form);
}

// Variantes de la ficha para las que no traen eac-perso-data (fichas sin
// personalizacion). Si /products/<handle>.js falla, se tira de ShopifyAnalytics
// (sin disponibilidad: el servidor rechazara una talla agotada al anadir).
async function loadProductVariants() {
  const m = window.location.pathname.match(/\/products\/([^/?#]+)/);
  if (m) {
    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const r = await fetch(`${root}products/${m[1]}.js`, { headers: { Accept: 'application/json' } });
      if (r.ok) {
        const p = await r.json();
        return {
          optionNames: (p.options || []).map((o) => (typeof o === 'string' ? o : o.name)),
          variants: (p.variants || []).map((v) => ({ id: v.id, price: v.price, available: v.available, options: v.options })),
        };
      }
    } catch (e) {
      /* abajo el plan B */
    }
  }
  const meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product;
  if (!meta || !meta.variants) throw new Error('sin variantes');
  const names = [];
  document.querySelectorAll('#EacPdpForm input[name^="options["]').forEach((inp) => {
    const n = inp.name.match(/^options\[(.+)\]$/)[1];
    if (names.indexOf(n) === -1) names.push(n);
  });
  return {
    optionNames: names,
    variants: meta.variants.map((v) => ({
      id: v.id,
      price: v.price,
      available: true,
      options: String(v.public_title || '').split(' / '),
    })),
  };
}

// Fichas SIN personalizacion: el form se envia nativo y nadie actualizaba su id.
// Las variantes salen de los datos de la seccion; si no estan, de /products/<handle>.js.
class VariantSync {
  constructor(form, data) {
    this.form = form;
    if (form.querySelector('input[name^="options["]') == null) return; // talla unica
    const source = data && data.variants && data.variants.length ? Promise.resolve(data) : loadProductVariants();
    this.ready = source.then(
      (d) => {
        this.variants = d.variants;
        this.optionNames = d.optionNames;
        this.data = Object.assign({}, data, { variants: d.variants, optionNames: d.optionNames });
        this.ownIds = new Set(this.variants.map((v) => Number(v.id)));
        applyVariant(form, findVariant(form, this.variants, this.optionNames), this.ownIds, false, this.data);
      },
      () => {
        this.failed = true;
      }
    );
    form.addEventListener('change', (e) => {
      if (!e.target.matches('input[name^="options["]')) return;
      hideFormError(form);
      clearMissing(e.target.closest('.pdp-opt'));
      this.ready.then(() => {
        if (this.variants) applyVariant(form, findVariant(form, this.variants, this.optionNames), this.ownIds, true, this.data);
      });
    });
    form.addEventListener('submit', (e) => this.onSubmit(e));
  }

  // Envio nativo con el id bueno; si aun no han llegado las variantes, se espera.
  onSubmit(e) {
    if (this.variants) {
      if (!this.check()) e.preventDefault();
      return;
    }
    if (this.failed) {
      if (!this.canSubmitBlind()) e.preventDefault();
      return;
    }
    e.preventDefault();
    this.ready.then(() => {
      if (this.variants ? this.check() : this.canSubmitBlind()) this.form.submit();
    });
  }

  // Sin forma de saber las variantes: el form sale como siempre solo si ya trae su
  // id (una sola variante o ?variant= en la URL); si no, se avisa en vez de mandarlo sin talla.
  canSubmitBlind() {
    const idInput = this.form.querySelector('input[name="id"]');
    if (idInput && !idInput.disabled && idInput.value) return true;
    showFormError(this.form, 'No hemos podido cargar las tallas. Recarga la página e inténtalo de nuevo.');
    revealFormError(this.form);
    return false;
  }

  check() {
    const v = findVariant(this.form, this.variants, this.optionNames);
    if (!v) {
      promptMissingOrUnavailable(this.form);
      return false;
    }
    if (!v.available) {
      showFormError(this.form, 'Esa talla está agotada. Elige otra.');
      revealFormError(this.form);
      return false;
    }
    applyVariant(this.form, v, this.ownIds, false, this.data);
    return true;
  }
}

// Tras anadir, que el cliente vea el carrito. Horizon solo abre el drawer si tiene el
// atributo auto-open (un ajuste del tema que aqui esta apagado): sin el, al anadir no
// pasaba nada visible (Mafer, 31/07: "se queda alli mismo").
function openCartDrawer() {
  const d = document.querySelector('cart-drawer-component');
  if (d && !d.hasAttribute('auto-open') && typeof d.open === 'function') d.open();
}

// El codigo del pack (PACK15) se lee del aviso legal que pinta la propia seccion.
function bundleDiscountCode(wrap) {
  if (!wrap) return null;
  if (wrap.dataset.discountCode) return wrap.dataset.discountCode;
  const legal = wrap.querySelector('.eac-bundle__legal');
  const m = legal && legal.textContent.match(/c[oó]digo\s+([A-Za-z0-9_-]+)/i);
  return m ? m[1] : null;
}

/* ---------- Disenador: sube tu diseno con preview ---------- */

class Designer {
  constructor(root, data) {
    this.root = root;
    this.data = data || {};
    this.required = root.dataset.required === 'true';
    this.fileInput = root.querySelector('[data-designer-file]');
    this.art = root.querySelector('[data-designer-art]');
    this.area = root.querySelector('[data-designer-area]');
    this.base = root.querySelector('[data-designer-base]');
    this.textEl = root.querySelector('[data-designer-text]');
    this.controls = root.querySelector('[data-designer-controls]');
    this.scaleInput = root.querySelector('[data-designer-scale]');
    this.posInput = root.querySelector('[data-designer-pos]');
    this.pdfNote = root.querySelector('[data-designer-pdf-note]');
    this.uploadLabel = root.querySelector('[data-designer-upload-label]');
    this.pattern = root.querySelector('[data-designer-pattern]');
    this.file = null;
    this.objectUrl = null;
    this.placement = 'chest';
    this.baseSrc = this.base ? this.base.src : '';
    this.pos = { x: 50, y: 50, w: 60 };      // arte: centro x/y y ancho, en % de la zona
    this.textPos = { x: 50, y: 82 };          // texto: centro x/y, en % de la zona
    this.motif = 26;                          // tamano del motivo del patron, en % del ancho
    this.fullStyle = 'tile';                  // 'tile' (motivo repetido) | 'single' (imagen entera)
    this.styleWrap = root.querySelector('[data-fullstyle-wrap]');
    this.maskUrl = null;
    this.lastGoodMask = null;                 // se reutiliza si la deteccion falla (misma silueta en otro color)
    this.maskDirty = true;
    this.bind();
  }

  bind() {
    if (this.fileInput) this.fileInput.addEventListener('change', () => this.onFile());
    if (this.scaleInput) {
      this.scaleInput.addEventListener('input', () => {
        if (this.placement === 'full') {
          // en estampado completo el slider controla la densidad del motivo
          this.motif = Math.round(Number(this.scaleInput.value) * 0.45); // 20-100 -> 9-45%
          this.renderPattern();
          this.serialize();
        } else {
          this.pos.w = Number(this.scaleInput.value);
          this.render();
        }
      });
    }
    // ubicacion del estampado (pecho / toda la camiseta)
    this.root.querySelectorAll('input[data-designer-placement]').forEach((r) => {
      r.addEventListener('change', () => {
        if (r.checked) this.setPlacement(r.dataset.designerPlacement);
      });
    });
    // estilo del estampado completo (motivo repetido / imagen entera)
    this.root.querySelectorAll('input[data-designer-fullstyle]').forEach((r) => {
      r.addEventListener('change', () => {
        if (!r.checked) return;
        this.fullStyle = r.dataset.designerFullstyle;
        this.renderPattern();
        this.serialize();
      });
    });
    this.bindDrag(this.art, this.pos, false);
    this.bindDrag(this.textEl, this.textPos, true);
  }

  // Cambia entre logo en el pecho y patron repetido por toda la prenda.
  setPlacement(mode) {
    this.placement = mode === 'full' ? 'full' : 'chest';
    this.area.classList.toggle('eac-designer__area--full', this.placement === 'full');
    const isImg = this.file && this.file.type !== 'application/pdf';
    if (this.styleWrap) this.styleWrap.hidden = this.placement !== 'full';
    if (this.placement === 'full') {
      if (this.art) this.art.hidden = true;
      if (this.pattern && isImg) this.pattern.hidden = false;
      if (this.scaleInput) this.scaleInput.value = Math.round(this.motif / 0.45);
      this.ensureMask();
      this.renderPattern();
    } else {
      if (this.pattern) this.pattern.hidden = true;
      if (this.art && isImg) this.art.hidden = false;
      if (this.scaleInput) this.scaleInput.value = this.pos.w;
      this.render();
    }
    this.serialize();
  }

  // Pinta el estampado completo: motivo repetido "a tresbolillo" (2 capas, la
  // segunda desplazada medio motivo) o una unica imagen que cubre la prenda.
  renderPattern() {
    if (!this.pattern || !this.objectUrl) return;
    const url = 'url(' + this.objectUrl + ')';
    if (this.fullStyle === 'single') {
      // imagen entera: una sola pieza centrada; el slider ajusta su ancho
      const size = Math.round(Number(this.scaleInput ? this.scaleInput.value : 50) * 2); // 20-100 -> 40-200%
      this.pattern.style.backgroundImage = url;
      this.pattern.style.backgroundRepeat = 'no-repeat';
      this.pattern.style.backgroundPosition = 'center';
      this.pattern.style.backgroundSize = size + '% auto';
      this.singleSize = size;
      return;
    }
    this.pattern.style.backgroundRepeat = 'repeat';
    const stage = this.pattern.parentElement;
    const w = stage ? stage.clientWidth : 0;
    if (w > 0) {
      const tile = Math.max(10, (w * this.motif) / 100);
      this.pattern.style.backgroundImage = url + ', ' + url;
      this.pattern.style.backgroundSize = tile + 'px auto, ' + tile + 'px auto';
      this.pattern.style.backgroundPosition = '0 0, ' + tile / 2 + 'px ' + tile / 2 + 'px';
    } else {
      this.pattern.style.backgroundImage = url;
      this.pattern.style.backgroundSize = this.motif + '%';
    }
  }

  // Genera una mascara con la silueta de la prenda (prenda = pixel saturado u
  // oscuro frente a fondo claro) para que el patron no pise el fondo.
  ensureMask() {
    if (!this.maskDirty) return;
    this.maskDirty = false;
    const self = this;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = function () {
      try {
        const w = 300;
        const h = Math.max(1, Math.round((w * img.naturalHeight) / img.naturalWidth));
        const cv = document.createElement('canvas');
        cv.width = w; cv.height = h;
        const ctx = cv.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        const src = ctx.getImageData(0, 0, w, h);
        const out = ctx.createImageData(w, h);
        let shirt = 0;
        for (let i = 0; i < src.data.length; i += 4) {
          const r = src.data[i], g = src.data[i + 1], b = src.data[i + 2];
          const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
          const lum = (mx + mn) / 2, sat = mx - mn;
          const isShirt = sat > 60 || lum < 100;
          if (isShirt) shirt++;
          out.data[i] = out.data[i + 1] = out.data[i + 2] = 255;
          out.data[i + 3] = isShirt ? 255 : 0;
        }
        const coverage = shirt / (w * h);
        if (coverage < 0.08 || coverage > 0.92) {
          // deteccion poco fiable (p.ej. prenda blanca sobre fondo blanco):
          // reutilizar la ultima silueta buena (los mockups por color comparten forma)
          self.maskUrl = self.lastGoodMask;
        } else {
          ctx.putImageData(out, 0, 0);
          const cv2 = document.createElement('canvas');
          cv2.width = w; cv2.height = h;
          const c2 = cv2.getContext('2d');
          c2.filter = 'blur(1.5px)';
          c2.drawImage(cv, 0, 0);
          self.maskUrl = cv2.toDataURL();
          self.lastGoodMask = self.maskUrl;
        }
      } catch (e) {
        self.maskUrl = self.lastGoodMask; // p.ej. canvas contaminado por CORS
      }
      self.applyMask();
    };
    img.onerror = function () { self.maskUrl = null; self.applyMask(); };
    img.src = this.base ? (this.base.currentSrc || this.base.src) : '';
  }

  applyMask() {
    if (!this.pattern) return;
    const st = this.pattern.style;
    if (this.maskUrl) {
      st.webkitMaskImage = 'url(' + this.maskUrl + ')';
      st.maskImage = 'url(' + this.maskUrl + ')';
      st.webkitMaskSize = '100% 100%';
      st.maskSize = '100% 100%';
      st.inset = '0';
    } else {
      // sin mascara fiable: limitar el patron al rectangulo de la prenda
      st.webkitMaskImage = '';
      st.maskImage = '';
      const rect = (this.area.dataset.areaFull || '22,8,56,84').split(',');
      if (rect.length === 4) {
        st.left = rect[0] + '%';
        st.top = rect[1] + '%';
        st.width = rect[2] + '%';
        st.height = rect[3] + '%';
        st.inset = 'auto';
      }
    }
  }

  // Texto en vivo sobre la prenda (contenido + color).
  setText(value, colorHex) {
    if (!this.textEl) return;
    const has = value && value.trim() !== '';
    this.textEl.hidden = !has;
    this.textEl.textContent = has ? value.trim() : '';
    if (colorHex) this.textEl.style.color = colorHex;
    this.renderText();
    this.serialize();
  }

  // Cambia el mockup segun el color elegido (convencion: alt contiene "color:<valor>").
  setShirtColor(colorValue) {
    if (!this.base || !colorValue) return;
    const imgs = this.data.images || [];
    const want = 'color:' + colorValue.trim().toLowerCase();
    const hit = imgs.find((i) => (i.alt || '').toLowerCase().indexOf(want) !== -1);
    const src = hit ? hit.src : this.baseSrc;
    if (this.base.src !== src) {
      this.base.src = src;
      const main = document.getElementById('EacPdpMain');
      if (main && hit) main.src = hit.src;
      // la silueta cambia con la foto: recalcular la mascara del patron
      this.maskDirty = true;
      if (this.placement === 'full') this.ensureMask();
    }
  }

  onFile() {
    const f = this.fileInput.files && this.fileInput.files[0];
    this.file = f || null;
    if (!f) {
      this.root.classList.remove('eac-designer--active');
      this.art.hidden = true;
      if (this.pattern) this.pattern.hidden = true;
      if (this.controls) this.controls.hidden = true;
      if (this.pdfNote) this.pdfNote.hidden = true;
      if (this.posInput) this.posInput.value = '';
      return;
    }
    this.root.classList.add('eac-designer--active');
    if (this.uploadLabel) this.uploadLabel.textContent = 'Cambiar diseño (' + f.name.slice(0, 24) + ')';
    if (f.type === 'application/pdf') {
      this.art.hidden = true;
      if (this.pattern) this.pattern.hidden = true;
      if (this.controls) this.controls.hidden = true;
      if (this.pdfNote) this.pdfNote.hidden = false;
      if (this.posInput) this.posInput.value = '';
      return;
    }
    if (this.pdfNote) this.pdfNote.hidden = true;
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
    this.objectUrl = URL.createObjectURL(f);
    this.art.src = this.objectUrl;
    if (this.controls) this.controls.hidden = false;
    this.setPlacement(this.placement); // repinta arte o patron segun el modo activo
  }

  render() {
    const st = this.art.style;
    st.width = this.pos.w + '%';
    st.height = 'auto';
    st.left = this.pos.x + '%';
    st.top = this.pos.y + '%';
    st.transform = 'translate(-50%, -50%)';
    this.renderText();
    this.serialize();
  }

  renderText() {
    if (!this.textEl || this.textEl.hidden) return;
    this.textEl.style.left = this.textPos.x + '%';
    this.textEl.style.top = this.textPos.y + '%';
  }

  serialize() {
    if (!this.posInput) return;
    let v = '';
    if (this.file) {
      if (this.placement === 'full' && this.fullStyle === 'single') {
        v = 'estampado completo imagen entera, ancho:' + (this.singleSize || 100) + '% de la prenda';
      } else if (this.placement === 'full') {
        v = 'estampado completo a tresbolillo, motivo:' + Math.round(this.motif) + '% del ancho';
      } else {
        v = 'arte x:' + Math.round(this.pos.x) + '% y:' + Math.round(this.pos.y) +
            '% ancho:' + Math.round(this.pos.w) + '% zona:pecho';
      }
    }
    if (this.textEl && !this.textEl.hidden) {
      v += (v ? ' | ' : '') + 'texto x:' + Math.round(this.textPos.x) + '% y:' + Math.round(this.textPos.y) + '%';
    }
    this.posInput.value = v;
  }

  bindDrag(el, pos, isText) {
    if (!el) return;
    let dragging = false;
    el.addEventListener('pointerdown', (e) => {
      dragging = true;
      try { el.setPointerCapture(e.pointerId); } catch (err) { /* no-op */ }
      e.preventDefault();
    });
    el.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      const r = this.area.getBoundingClientRect();
      if (!r.width || !r.height) return;
      pos.x = Math.min(100, Math.max(0, ((e.clientX - r.left) / r.width) * 100));
      pos.y = Math.min(100, Math.max(0, ((e.clientY - r.top) / r.height) * 100));
      if (isText) { this.renderText(); this.serialize(); } else { this.render(); }
    });
    el.addEventListener('pointerup', () => { dragging = false; });
    el.addEventListener('pointercancel', () => { dragging = false; });
  }
}

/* ---------- PDP ---------- */

class PersoPdp {
  constructor(form, data) {
    this.form = form;
    this.data = data || JSON.parse(document.getElementById('eac-perso-data').textContent);
    this.variants = this.data.variants || [];
    this.optionNames = this.data.optionNames || [];
    this.ownIds = new Set(this.variants.map((v) => Number(v.id)));
    this.totalEl = form.querySelector('[data-eac-total]');
    const designerEl = document.getElementById('EacDesigner');
    this.designer = designerEl ? new Designer(designerEl, this.data) : null;
    this.bind();
    this.refresh();
  }

  bind() {
    this.form.addEventListener('change', (e) => this.refresh(e));
    this.form.addEventListener('input', () => this.refresh());
    this.form.addEventListener('submit', (e) => this.onSubmit(e));
    // mostrar/ocultar el cuerpo de un extra (p.ej. color de las estrellas)
    this.form.querySelectorAll('input[data-extra-toggle]').forEach((chk) => {
      const body = this.form.querySelector('[data-extra-body="' + chk.dataset.extraToggle + '"]');
      if (!body) return;
      const sync = () => { body.hidden = !chk.checked; };
      chk.addEventListener('change', sync);
      sync();
    });
  }

  // Resuelve la variante seleccionada a partir de los radios options[] (null si
  // falta alguna opcion: antes caia en silencio a la primera talla).
  resolveVariant() {
    return findVariant(this.form, this.variants, this.optionNames);
  }

  // Valor marcado de una opcion aunque aun falte otra (p.ej. el color antes que la talla).
  checkedOption(name) {
    let value = null;
    this.form.querySelectorAll('input[name^="options["]:checked').forEach((inp) => {
      const m = inp.name.match(/^options\[(.+)\]$/);
      if (m && m[1] === name) value = inp.value;
    });
    return value;
  }

  // Suma de sobreprecios activos (en centimos) + lista de fees activos.
  activeFees() {
    const fees = [];
    const tej = this.form.querySelector('input[data-fee-variant][name="properties[Tejido]"]:checked');
    if (tej && tej.dataset.feeVariant) {
      fees.push({ variant: Number(tej.dataset.feeVariant), price: Number(tej.dataset.feePrice), label: tej.dataset.feeLabel || tej.value });
    }
    const texto = this.form.querySelector('[data-eac-texto]');
    if (texto && texto.value.trim() !== '' && texto.dataset.feeVariant) {
      fees.push({ variant: Number(texto.dataset.feeVariant), price: Number(texto.dataset.feePrice), label: texto.dataset.feeLabel || 'Texto personalizado' });
    }
    // extras con checkbox (forro, estrellas...)
    this.form.querySelectorAll('input[data-fee-extra]:checked').forEach((chk) => {
      if (!chk.dataset.feeVariant) return;
      fees.push({ variant: Number(chk.dataset.feeVariant), price: Number(chk.dataset.feePrice), label: chk.dataset.feeLabel || chk.name });
    });
    return fees;
  }

  refresh(e) {
    const v = this.resolveVariant();
    const byOption = !!(e && e.target && e.target.matches && e.target.matches('input[name^="options["]'));
    if (byOption) {
      hideFormError(this.form);
      clearMissing(e.target.closest('.pdp-opt'));
    }
    applyVariant(this.form, v, this.ownIds, byOption, this.data);
    const base = v ? v.price : this.data.basePrice;
    const extra = this.activeFees().reduce((s, f) => s + f.price, 0);
    if (this.totalEl) this.totalEl.textContent = formatMoney(base + extra);

    // texto en vivo: mostrar swatches de color y overlay sobre la prenda
    const texto = this.form.querySelector('[data-eac-texto]');
    const colorWrap = this.form.querySelector('[data-texto-color]');
    const colorSel = this.form.querySelector('input[name="properties[Color texto]"]:checked');
    const hasText = texto && texto.value.trim() !== '';
    if (colorWrap) colorWrap.hidden = !hasText;
    if (this.designer) {
      this.designer.setText(hasText ? texto.value : '', colorSel ? colorSel.dataset.textoColorHex : null);
      // color de camiseta: si la variante tiene opcion "Color" y hay mockup para ese color
      // (tambien antes de elegir la talla: el mockup cambia en cuanto se marca el color)
      const ci = this.optionNames.findIndex((n) => n.toLowerCase().indexOf('color') !== -1);
      const color = ci === -1 ? null : v ? v.options[ci] : this.checkedOption(this.optionNames[ci]);
      if (color != null) this.designer.setShirtColor(color);
    }
  }

  onSubmit(e) {
    e.preventDefault();
    const built = this.buildItems();
    if (built) this.addToCart(built.items, built.variantId, built.qty, built.designFile);
  }

  // Valida la ficha y arma las lineas (producto + fees). null si falta algo; el
  // aviso ya queda pintado. qty fija la cantidad (el pack lleva 1 de cada).
  buildItems(fixedQty) {
    const v = this.resolveVariant();
    if (!v) {
      promptMissingOrUnavailable(this.form);
      return null;
    }
    if (!v.available) {
      this.showError('Esa talla está agotada. Elige otra.');
      revealFormError(this.form);
      return null;
    }

    // Validacion tejido obligatorio (si la seccion lo marca)
    const reqTejido = this.form.querySelector('.eac-perso__tejido[data-required="true"]');
    if (reqTejido && !reqTejido.querySelector('input[name="properties[Tejido]"]:checked')) {
      this.showError('Selecciona un tejido para continuar.');
      reqTejido.scrollIntoView({ behavior: scrollBehavior(), block: 'center' });
      return null;
    }

    // Validacion diseno obligatorio (productos con disenador)
    if (this.designer && this.designer.required && !this.designer.file) {
      this.showError('Sube tu diseño para continuar.');
      this.designer.root.scrollIntoView({ behavior: scrollBehavior(), block: 'center' });
      return null;
    }

    const qtyInput = this.form.querySelector('input[name="quantity"]');
    const qty = fixedQty || Math.max(1, Number((qtyInput && qtyInput.value) || 1));
    const persoId = genPersoId();
    const fees = this.activeFees();

    // Propiedades visibles del producto principal
    const mainProps = { [PERSO_ID_PROP]: persoId };
    const tej = this.form.querySelector('input[name="properties[Tejido]"]:checked');
    if (tej) mainProps['Tejido'] = tej.value;
    const texto = this.form.querySelector('[data-eac-texto]');
    if (texto && texto.value.trim() !== '') mainProps['Texto'] = texto.value.trim();
    // otras props libres del form (Frase, Notas, extras, posicion del diseno, etc.)
    const hasTexto = texto && texto.value.trim() !== '';
    this.form.querySelectorAll('[name^="properties["]').forEach((inp) => {
      if (inp === tej || inp === texto) return;
      if (inp.type === 'file') return; // el archivo va aparte (FormData)
      if (inp.name === 'properties[Tejido]') return;
      if (inp.name === 'properties[Color texto]' && !hasTexto) return; // color solo si hay texto
      if ((inp.type === 'radio' || inp.type === 'checkbox') && !inp.checked) return;
      if (inp.closest('[hidden]')) return; // dentro de un bloque oculto/desactivado
      const m = inp.name.match(/^properties\[(.+)\]$/);
      if (m && inp.value && inp.value.trim() !== '') mainProps[m[1]] = inp.value;
    });

    const items = [{ id: Number(v.id), quantity: qty, properties: mainProps }];
    fees.forEach((f) => {
      items.push({
        id: f.variant,
        quantity: qty,
        properties: {
          [PERSO_ID_PROP]: persoId,
          [FEE_PARENT_PROP]: String(v.id),
          [FEE_LABEL_PROP]: f.label,
        },
      });
    });

    const designFile = this.designer && this.designer.file ? this.designer.file : null;
    return { items, variantId: v.id, qty, designFile };
  }

  showError(msg) {
    showFormError(this.form, msg);
  }

  // Pack "comprados juntos": lleva la prenda tal cual esta configurada (talla,
  // tejido, texto, diseno) + los complementos del pack. Antes el pack metia la
  // primera talla y sin la personalizacion que el cliente habia elegido.
  async addBundle(btn) {
    const wrap = btn.closest('.eac-bundle-wrap');
    const msg = wrap ? wrap.querySelector('[data-bundle-msg]') : null;
    const missing = wrap?.querySelector('[data-bundle-variant]:invalid');
    if (missing) {
      if (msg) { msg.textContent = 'Elige la talla u opción de cada complemento.'; msg.hidden = false; }
      missing.reportValidity();
      return;
    }
    const built = this.buildItems(1);
    if (!built) return; // buildItems ya ha avisado y ha traido el aviso a la vista
    const tag = 'juntos-' + (wrap && wrap.id ? wrap.id.replace(/^EacBundle-/, '') : 'pack');
    built.items[0].properties._bundle = tag;
    const extras = Array.from(wrap ? wrap.querySelectorAll('.eac-bundle__item[data-vid]') : [])
      .map((el) => Number(el.dataset.vid))
      .filter((id) => id && !this.ownIds.has(id))
      .map((id) => ({ id, quantity: 1, properties: { _bundle: tag } }));
    const code = bundleDiscountCode(wrap);
    const redirect = 1 + extras.length >= 3 && code ? '/discount/' + encodeURIComponent(code) + '?redirect=/cart' : '/cart';
    const label = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Añadiendo…';
    if (msg) msg.hidden = true;
    const ok = await this.addToCart(built.items.concat(extras), built.variantId, built.qty, built.designFile, {
      redirect,
      onError: (m) => {
        if (!msg) return this.showError(m);
        msg.textContent = m;
        msg.hidden = false;
      },
    });
    if (!ok) {
      btn.disabled = false;
      btn.textContent = label;
    }
  }

  // opts.redirect: tras anadir, ir a esa URL (pack) en vez de abrir el drawer.
  // opts.onError: donde pintar el aviso. Devuelve true si todo entro.
  async addToCart(items, variantId, qty, designFile, opts = {}) {
    const fail = (m) => (opts.onError ? opts.onError(m) : this.showError(m));
    const btn = this.form.querySelector('.pdp-atc');
    if (btn) btn.setAttribute('aria-busy', 'true');
    const sections = cartItemsSectionIds().join(',');
    try {
      let res;
      if (designFile) {
        // Con archivo: el producto principal va en multipart (Shopify sube el
        // archivo a su CDN y guarda la URL en la property "Diseño").
        const main = items[0];
        const fd = new FormData();
        fd.append('id', String(main.id));
        fd.append('quantity', String(main.quantity));
        Object.keys(main.properties || {}).forEach((k) => fd.append('properties[' + k + ']', main.properties[k]));
        fd.append('properties[Diseño]', designFile, designFile.name);
        const feeItems = items.slice(1);
        if (!feeItems.length) fd.append('sections', sections);
        const r1 = await fetch(Theme.routes.cart_add_url, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: fd,
        });
        res = await r1.json();
        if (res.status) {
          fail(res.description || res.message || 'No se pudo añadir al carrito.');
          return false;
        }
        if (feeItems.length) {
          const r2 = await fetch(Theme.routes.cart_add_url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: feeItems, sections, sections_url: window.location.pathname }),
          });
          res = await r2.json();
          if (res.status) {
            fail(res.description || res.message || 'No se pudo añadir el suplemento.');
            return false;
          }
        }
      } else {
        const r = await fetch(Theme.routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections, sections_url: window.location.pathname }),
        });
        res = await r.json();
        if (res.status) {
          fail(res.description || res.message || 'No se pudo añadir al carrito.');
          return false;
        }
      }
      if (opts.redirect) {
        window.location.href = opts.redirect;
        return true;
      }
      const cart = await getCart();
      document.dispatchEvent(
        new CartAddEvent(cart, 'eac-perso', {
          source: 'eac-perso',
          itemCount: qty,
          variantId: String(variantId),
          sections: res.sections,
        })
      );
      openCartDrawer();
      // El cajon guarda su desplazamiento al cerrarse: quien bajo por el y anade otra talla no veia la
      // linea nueva, que entra arriba (26/10). Como en el "+" de las sugerencias, que se vea lo anadido.
      revealDrawerLineSoon(String(variantId));
      return true;
    } catch (e) {
      fail('Error de conexion. Intentalo de nuevo.');
      return false;
    } finally {
      if (btn) btn.removeAttribute('aria-busy');
    }
  }
}

/* ---------- Carrito: ocultar + reconciliar fees ---------- */

const CartPerso = {
  busy: false,

  injectStyle() {
    if (document.getElementById('eac-perso-cart-style')) return;
    const s = document.createElement('style');
    s.id = 'eac-perso-cart-style';
    s.textContent =
      '.cart-items__table-row[data-eac-fee]{display:none !important;}' +
      '.eac-perso-note{font-size:11px;color:var(--brand-grey-text,#636563);margin-top:4px;line-height:1.4;}';
    document.head.appendChild(s);
  },

  isFee(item) {
    return item.properties && item.properties[FEE_PARENT_PROP] != null;
  },

  groupKey(item) {
    return item.properties ? item.properties[PERSO_ID_PROP] : null;
  },

  async reconcile() {
    if (this.busy) return;
    let cart;
    try {
      cart = await getCart();
    } catch (e) {
      return;
    }
    const items = cart.items || [];
    // mapa persoId -> { parentQty, fees:[{key,qty}] }
    const groups = {};
    items.forEach((it) => {
      const gid = this.groupKey(it);
      if (!gid) return;
      groups[gid] = groups[gid] || { parentQty: null, fees: [] };
      if (this.isFee(it)) groups[gid].fees.push({ key: it.key, qty: it.quantity });
      else groups[gid].parentQty = it.quantity;
    });

    const changes = [];
    Object.keys(groups).forEach((gid) => {
      const g = groups[gid];
      g.fees.forEach((fee) => {
        const target = g.parentQty == null ? 0 : g.parentQty; // padre fuera -> borrar
        if (fee.qty !== target) changes.push({ id: fee.key, quantity: target });
      });
    });

    if (changes.length) {
      this.busy = true;
      try {
        let res = null;
        for (const ch of changes) {
          res = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              id: ch.id,
              quantity: ch.quantity,
              sections: cartItemsSectionIds().join(','),
              sections_url: window.location.pathname,
            }),
          }).then((r) => r.json());
        }
        if (res) {
          document.dispatchEvent(
            new CartAddEvent(res, 'eac-perso-reconcile', { source: 'eac-perso', sections: res.sections })
          );
        }
      } finally {
        this.busy = false;
      }
      return; // el evento disparara otra pasada que ya ocultara
    }

    this.decorate(items);
  },

  // Oculta filas-fee y anota el sobreprecio bajo el producto padre.
  decorate(items) {
    const feeKeys = [];
    const parentNotes = {}; // persoId -> [labels]
    items.forEach((it) => {
      if (this.isFee(it)) {
        feeKeys.push(it.key);
        const gid = this.groupKey(it);
        const label = it.properties[FEE_LABEL_PROP] || 'Personalización';
        (parentNotes[gid] = parentNotes[gid] || []).push(label + ' +' + formatMoney(it.price));
      }
    });
    feeKeys.forEach((key) => {
      const row = document.querySelector(`.cart-items__table-row[data-key="${key}"]`);
      if (row) row.setAttribute('data-eac-fee', '');
    });
    // anotar padres
    items.forEach((it) => {
      if (this.isFee(it)) return;
      const gid = this.groupKey(it);
      if (!gid || !parentNotes[gid]) return;
      const row = document.querySelector(`.cart-items__table-row[data-key="${it.key}"]`);
      if (!row) return;
      const host = row.querySelector('.cart-items__product-info') || row.querySelector('.cart-items__details');
      if (!host) return;
      let note = row.querySelector('.eac-perso-note');
      if (!note) {
        note = document.createElement('div');
        note.className = 'eac-perso-note';
        host.appendChild(note);
      }
      note.textContent = 'Personalización: ' + parentNotes[gid].join(' · ');
    });
  },

  init() {
    this.injectStyle();
    const run = () => this.reconcile();
    document.addEventListener(ThemeEvents.cartUpdate, () => {
      // tras el morph del tema
      requestAnimationFrame(() => setTimeout(run, 30));
    });
    if (document.readyState !== 'loading') run();
    else document.addEventListener('DOMContentLoaded', run);
  },
};

/* ---------- upsell del drawer: "Completa tu carrito" ---------- */

// Tras anadir desde el cajon, que se vea lo anadido (26/10: "anado y no aparece"). La linea
// nueva entra arriba de la lista y el cajon conservaba su desplazamiento: el cliente seguia
// viendo las sugerencias y su producto quedaba por encima, fuera de la vista.
function drawerScrollBox() {
  return document.querySelector('cart-drawer-component .cart-drawer__content');
}

function revealDrawerLine(variantId) {
  const box = drawerScrollBox();
  if (!box) return false;
  const row = box.querySelector(`.cart-drawer__items [data-key^="${variantId}:"]`);
  if (!row) return false;
  const b = box.getBoundingClientRect();
  const r = row.getBoundingClientRect();
  const summary = box.querySelector('.cart-drawer__summary');
  const bottom = summary ? Math.min(b.bottom, summary.getBoundingClientRect().top) : b.bottom;
  if (r.top >= b.top && r.bottom <= bottom) return true; // ya se ve entera
  // La primera linea, arriba del todo: asi tambien se ve la barra del envio gratis.
  const first = box.querySelector('.cart-drawer__items [data-key]:not([data-eac-fee])');
  const top = row === first ? 0 : box.scrollTop + r.top - b.top - 8;
  box.scrollTo({ top: Math.max(0, top), behavior: scrollBehavior() });
  return true;
}

// El cajon se repinta al recibir el evento; si la linea aun no esta, se reintenta un momento.
function revealDrawerLineSoon(variantId) {
  let tries = 0;
  const attempt = () => {
    if (revealDrawerLine(variantId)) return;
    if (++tries <= 4) {
      setTimeout(attempt, 250);
      return;
    }
    const box = drawerScrollBox();
    if (box) box.scrollTo({ top: 0, behavior: scrollBehavior() }); // las lineas nuevas entran arriba
  };
  requestAnimationFrame(() => requestAnimationFrame(attempt));
}

// Si no se pudo anadir, se dice junto al producto (antes el boton volvia a "+" sin mas).
function upsellError(btn, message) {
  const item = btn.closest('.eac-upsell__item');
  const info = item ? item.querySelector('.eac-upsell__info') : null;
  if (!info || info.querySelector('.pdp-atc-error')) return;
  const p = document.createElement('p');
  p.className = 'pdp-atc-error';
  p.setAttribute('role', 'alert');
  p.textContent = message || 'Error al agregar al carrito';
  info.appendChild(p);
  setTimeout(() => p.remove(), 4000);
}

function bindUpsell() {
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-eac-upsell-add]');
    if (!btn) return;
    e.preventDefault();
    const choice = btn.closest('.eac-upsell__item')?.querySelector('[data-eac-upsell-variant]');
    const vid = choice ? choice.value : btn.dataset.eacUpsellAdd;
    if (!vid) {
      upsellError(btn, 'Elige una talla u opción antes de añadir.');
      choice?.focus();
      return;
    }
    let errMsg = ''; // solo el texto que da Shopify (va en el idioma de la tienda)
    btn.disabled = true;
    btn.textContent = '…';
    try {
      const r = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(vid), quantity: 1 }],
          sections: cartItemsSectionIds().join(','),
          sections_url: window.location.pathname,
        }),
      });
      const res = await r.json();
      if (!r.ok || res.status) {
        errMsg = res.description || '';
        throw new Error('cart/add');
      }
      const cart = await getCart();
      document.dispatchEvent(new CartAddEvent(cart, 'eac-upsell', { source: 'eac-upsell', sections: res.sections }));
      revealDrawerLineSoon(vid);
      // El repintado suele cambiar este boton por el de otra sugerencia; si sigue aqui, vuelve a "+".
      setTimeout(() => {
        if (btn.isConnected && btn.disabled) {
          btn.disabled = false;
          btn.textContent = '+';
        }
      }, 1500);
    } catch (err) {
      btn.disabled = false;
      btn.textContent = '+';
      upsellError(btn, errMsg);
    }
  });
}

/* ---------- arranque ---------- */

// Datos de la ficha que pinta la seccion (variantes, precios, textos). null si no estan.
function readPdpData() {
  const el = document.getElementById('eac-perso-data');
  if (!el) return null;
  try {
    return JSON.parse(el.textContent);
  } catch (e) {
    return null;
  }
}

function boot() {
  const form = document.getElementById('EacPdpForm');
  const data = form ? readPdpData() : null;
  let pdp = null;
  let sync = null;
  // persoActive llega desde oct-2026; sin el (HTML anterior) los datos solo venian
  // en las fichas con personalizacion.
  if (form && data && data.persoActive !== false) {
    try {
      pdp = new PersoPdp(form, data);
    } catch (e) {
      /* si falla, el form nativo sigue funcionando (con la talla sincronizada abajo) */
    }
  }
  if (form && !pdp) sync = new VariantSync(form, data);
  if (form) {
    // El pack trae su propio boton e inline script; en fase de captura se adelanta
    // para que la prenda entre con su talla (y su personalizacion). Sin talla
    // elegida no sale: se avisa en el grupo que falta.
    document.addEventListener(
      'click',
      (e) => {
        const btn = e.target.closest('[data-add-bundle]');
        if (!btn) return;
        if (pdp) {
          e.preventDefault();
          e.stopImmediatePropagation();
          pdp.addBundle(btn);
          return;
        }
        if (!sync || !sync.variants) return; // talla unica o sin datos: el pack va como siempre
        const v = findVariant(form, sync.variants, sync.optionNames);
        if (v && v.available) {
          applyVariant(form, v, sync.ownIds, false, sync.data);
          return; // el script del pack manda la talla elegida
        }
        e.preventDefault();
        e.stopImmediatePropagation();
        sync.check();
      },
      true
    );
  }
  CartPerso.init();
  bindUpsell();
}

if (document.readyState !== 'loading') boot();
else document.addEventListener('DOMContentLoaded', boot);
